import React, { useState } from 'react';
import { TrendingUp, Users, MessageSquare, Award, Zap, Brain } from 'lucide-react';
import ROIDashboard from './tabs/ROIDashboard';
import AutonomyMetrics from './tabs/AutonomyMetrics';
import AgentConversations from './tabs/AgentConversations';
import SupplierAccountability from './tabs/SupplierAccountability';
import AgentManager from './AgentManager';

const TABS = [
  {
    id: 'roi',
    label: 'Results',
    icon: TrendingUp,
    description: 'ROI, margin recovered, labor freed',
    section: 'results'
  },
  {
    id: 'manager',
    label: 'How It Works',
    icon: Brain,
    description: 'Ask the Agent Manager anything',
    section: 'how'
  },
  {
    id: 'autonomy',
    label: 'Agent Autonomy',
    icon: Users,
    description: 'Decision metrics & quality',
    section: 'operations'
  },
  {
    id: 'conversations',
    label: 'Direct Chat',
    icon: MessageSquare,
    description: 'Talk to individual agents',
    section: 'operations'
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: Award,
    description: 'Performance & accountability',
    section: 'operations'
  },
];

export default function MetricsTabs({ agents, selectedAgent, onSelectAgent, onSendMessage }) {
  const [activeTab, setActiveTab] = useState('roi');
  const [showOnboarding, setShowOnboarding] = useState(true);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'manager':
        return <AgentManager />;
      case 'roi':
        return <ROIDashboard />;
      case 'autonomy':
        return <AutonomyMetrics />;
      case 'conversations':
        return (
          <AgentConversations
            agents={agents}
            selectedAgent={selectedAgent}
            onSelectAgent={onSelectAgent}
            onSendMessage={onSendMessage}
          />
        );
      case 'suppliers':
        return <SupplierAccountability />;
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#050506]">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 border border-white/20 rounded-lg max-w-md p-8 backdrop-blur">
            <div className="text-center mb-6">
              <p className="text-3xl mb-3">🤖</p>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to Grocera</h2>
              <p className="text-white/70">Agentic AI that runs your procurement</p>
            </div>

            <div className="space-y-4 mb-8 text-sm text-white/80">
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">📊</span>
                <div>
                  <p className="font-medium text-white">6 Autonomous Agents</p>
                  <p className="text-white/50">Demand, Ordering, Claims, Receiving, Scoring, Confirmation</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">💰</span>
                <div>
                  <p className="font-medium text-white">$87K+ Monthly Recovery</p>
                  <p className="text-white/50">Margin recovered from short-ships & errors</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">⚡</span>
                <div>
                  <p className="font-medium text-white">96% Autonomous</p>
                  <p className="text-white/50">Only escalates exceptions that need humans</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">🏪</span>
                <div>
                  <p className="font-medium text-white">Live on 6 Indonesian Stores</p>
                  <p className="text-white/50">Total Buah Segar pilot → proven ROI</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowOnboarding(false)}
              className="w-full bg-[#10b981] hover:bg-[#0da76f] text-black font-medium py-2 rounded-lg transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      )}

      {/* Metrics Navbar */}
      <div className="bg-white/[0.02] border-b border-white/5 px-6 py-3 sticky top-0 z-20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-white/50 text-xs font-medium">PROOF POINT:</p>
            <p className="text-white text-xs">Total Buah Segar (6 stores, Indonesia) — $87K recovered, 2.1 FTE freed</p>
          </div>
          <button
            onClick={() => setShowOnboarding(true)}
            className="text-white/40 hover:text-white/60 text-xs transition-colors"
          >
            ℹ️
          </button>
        </div>
        <div className="grid grid-cols-5 gap-4">
          <div>
            <p className="text-white/50 text-xs">Autonomy Score</p>
            <p className="text-white font-bold text-lg">96%</p>
          </div>
          <div>
            <p className="text-white/50 text-xs">Decisions (Week)</p>
            <p className="text-[#10b981] font-bold text-lg">1,247</p>
          </div>
          <div>
            <p className="text-white/50 text-xs">Margin Recovered</p>
            <p className="text-white font-bold text-lg">$87.4K</p>
          </div>
          <div>
            <p className="text-white/50 text-xs">Labor Freed</p>
            <p className="text-white font-bold text-lg">2.1 FTE</p>
          </div>
          <div>
            <p className="text-white/50 text-xs">Active Agents</p>
            <p className="text-white font-bold text-lg">6/6</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation with Section Headers */}
      <div className="border-b border-white/5 bg-[#050506]/85 backdrop-blur overflow-x-auto">
        <div className="flex">
          {TABS.map((tab, idx) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const showSectionDivider = idx > 0 && TABS[idx - 1].section !== tab.section;

            return (
              <div key={tab.id} className="flex">
                {showSectionDivider && <div className="border-l border-white/5 self-stretch" />}
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-max px-6 py-4 flex items-center gap-2 transition-colors border-b-2 ${
                    isActive
                      ? 'border-b-[#10b981] text-white'
                      : 'border-b-transparent text-white/60 hover:text-white/80'
                  }`}
                >
                  <Icon size={18} />
                  <div className="text-left">
                    <p className="text-sm font-medium">{tab.label}</p>
                    <p className="text-[11px] text-white/40">{tab.description}</p>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">{renderTabContent()}</div>
    </div>
  );
}
